import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import HTLoader from "../../common/ht-loader/HTLoader";
import CorridorCard from "./action-cards/CorridorCard";
import CorridorRequestCard from "./action-cards/CorridorRequestCard";
import AriCard from "./action-cards/AriCard";
import PromoCard from "./action-cards/PromoCard";
import CampaignCard from "./action-cards/CampaignCard";
import PromoExpiryCard from "./action-cards/PromoExpiryCard";
import LlmActionCard from "./action-cards/LlmActionCard";
import BnberryCard from "./action-cards/BnberryCard";
import { dismissPromoExpiry } from "../../../api";
import { llmCardKey, isRequestedCard } from "./actionCardOrder";
import { buildActionCards, CARD_TYPE } from "./buildActionCards";
import { useActionCenterOptional } from "../../../contexts/ActionCenterContext";
import KnowledgeBaseCard from "./action-cards/KnowledgeBaseCard";
import BlogCard from "./action-cards/BlogCard";
import ChevronLeftIcon from "../../../assets/img/chevron-left.svg?react";
import ChevronRightIcon from "../../../assets/img/chevron-right.svg?react";
import "./ActionCenter.scss";

const MAX_CARDS = 3;
const CARDS_PER_SLIDE = 3;

const ActionCenter = ({
  data = null,
  v2Data = null,
  loading = false,
  v2Loading = false,
  // Both feeds' first load has finished (success or failure). Gates the
  // header count the same way the nav badge is gated in Root — the v1 read
  // usually lands first, and a count shown then visibly jumps when v2
  // arrives (EO-1834). The hooks latch this flag per property (a refetch
  // does not reset it), so the count holds steady through onPromoUsed
  // refetches just like the cards do. Defaults to true so callers that
  // don't track settlement keep today's behaviour.
  feedsSettled = true,
  selectedProperty,
  onPromoUsed,
  // EO-1184: an expiry email link opens the card's Review modal on arrival.
  autoOpenExpiryReview = false,
  onExpiryReviewOpened,
  // EO-1893: the v2 card an email deep link names renders first.
  pinnedRecommendationId = null,
  // EO-1892: the strip / pop-up CTA opens this v2 card's Create Promo panel.
  openRecommendationId = null,
  onRecommendationOpened,
  // Told which v2 card was dismissed, so the page can drop what points at it.
  onRecommendationDismissed,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Dismissals are shared with the layout so the nav badge drops in step with the
  // page header (EO-1907). Outside a provider — the component's own unit tests —
  // this is null and we fall back to local state, preserving standalone behaviour.
  const shared = useActionCenterOptional();
  const [localDismissed, setLocalDismissed] = useState(() => new Set());
  const dismissedCards = shared ? shared.dismissedKeys : localDismissed;

  const handleDismiss = useCallback(
    (key) => {
      if (shared) {
        shared.dismiss(key);
      } else {
        setLocalDismissed((prev) => new Set(prev).add(key));
      }
    },
    [shared]
  );

  const hotelName = selectedProperty?.propertyName || null;

  // Show loading only on initial load, not on refetch (to avoid unmounting cards).
  // The v1 and v2 feeds are independent reads, so each renders as soon as its OWN
  // fetch settles — the proven v1 cards must never sit behind the loader waiting on
  // the higher-latency v2 (LLM) read, nor the reverse. The loader therefore shows
  // only while both are still pending, i.e. there is genuinely nothing to render.
  // A failed fetch leaves its data null and so reads as pending forever, which is
  // harmless: the other feed arriving is what clears the loader.
  const v1Pending = loading || data === null;
  const v2Pending = v2Loading || v2Data === null;
  const showLoading = v1Pending && v2Pending;

  // The single source of truth for WHICH action cards render, in WHAT order, and
  // HOW MANY there are (v1/v2 dedup, dismissals, PROMO_EXPIRY, deep-link pinning,
  // BnBerry placement) lives in buildActionCards — shared with the nav badge so the
  // header count and the badge count can never drift (EO-1907). This component only
  // turns each descriptor into its card component and layers on fallback cards,
  // carousel, and cardPosition — none of which change the count.
  const descriptors = useMemo(
    () =>
      buildActionCards({
        data,
        v2Data,
        dismissedKeys: dismissedCards,
        pinnedKey: llmCardKey(pinnedRecommendationId),
      }),
    [data, v2Data, dismissedCards, pinnedRecommendationId]
  );

  // Rebuilds one descriptor into its card element. All interaction wiring —
  // dismissals, refetch, deep-link auto-open, the promo-expiry backend write —
  // stays here; the builder is kept pure. cardPosition is assigned from the final
  // index below, so it isn't passed here.
  const descriptorToElement = (descriptor) => {
    const { key, type, payload } = descriptor;
    switch (type) {
      case CARD_TYPE.CAMPAIGN:
        return (
          <CampaignCard
            key={key}
            data={[payload.rec]}
            selectedProperty={selectedProperty}
            onDismiss={() => handleDismiss(key)}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.PROMO_EXPIRY:
        return (
          <PromoExpiryCard
            key={key}
            autoOpenReview={autoOpenExpiryReview}
            onReviewAutoOpened={onExpiryReviewOpened}
            data={payload.data}
            onDismiss={(recommendationIds) => {
              handleDismiss(key);
              // Persist it too, so the dismissal survives a reload — unlike the in-memory default.
              // Sends every id in the payload, including rows hidden behind "+ N more", plus the
              // propertyId the backend requires to authorize the write.
              if (recommendationIds?.length && selectedProperty?.propertyId) {
                dismissPromoExpiry(
                  recommendationIds,
                  selectedProperty.propertyId
                ).catch(() => {});
              }
            }}
            // Same refetch the promo cards use when a promo is applied: both feeds, so the card list
            // is rebuilt from the server rather than patched in place.
            onRefresh={onPromoUsed}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.CORRIDOR_REQUEST:
        return (
          <CorridorRequestCard
            key={key}
            data={payload.request}
            onDismiss={() => handleDismiss(key)}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.PROMO:
        return (
          <PromoCard
            key={key}
            data={[payload.promoGroup]}
            selectedProperty={selectedProperty}
            onDismiss={() => handleDismiss(key)}
            onPromoUsed={onPromoUsed}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.CORRIDOR:
        return (
          <CorridorCard
            key={key}
            data={payload.corridorData}
            propertyId={payload.propertyId}
            onDismiss={() => handleDismiss(key)}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.ARI:
        return (
          <AriCard
            key={key}
            data={payload.ariData}
            onDismiss={() => handleDismiss(key)}
            hotelName={hotelName}
          />
        );
      case CARD_TYPE.LLM:
        return (
          <LlmActionCard
            key={key}
            card={payload.card}
            selectedProperty={selectedProperty}
            onDismiss={() => {
              handleDismiss(key);
              onRecommendationDismissed?.(payload.card?.recommendationId);
            }}
            onPromoUsed={onPromoUsed}
            hotelName={hotelName}
            autoOpenCta={isRequestedCard(payload.card, openRecommendationId)}
            onCtaAutoOpened={onRecommendationOpened}
          />
        );
      case CARD_TYPE.BNBERRY:
        return (
          <BnberryCard
            key={key}
            data={payload.bnberry}
            onDismiss={() => handleDismiss(key)}
            hotelName={hotelName}
          />
        );
      default:
        return null;
    }
  };

  // Ordering (including the deep-link pin) is already settled by the builder, so
  // the component just maps descriptors to elements.
  const actionCards = descriptors.map(descriptorToElement);

  const actionCount = actionCards.length;

  // Add fallback cards if needed (when action cards <= MAX_CARDS). cardPosition is assigned
  // from the final index below.
  const fallbackCards = [
    <KnowledgeBaseCard key="kb" hotelName={hotelName} />,
    <BlogCard key="blog" hotelName={hotelName} />,
  ];
  const needsFallback = actionCards.length <= MAX_CARDS;
  const remaining = needsFallback ? MAX_CARDS - actionCards.length : 0;
  const allCards = [
    ...actionCards,
    ...fallbackCards.slice(0, Math.max(0, remaining)),
  ];

  // Assign card_position from the FINAL ordering (after the sequence flatten and the BnBerry
  // splice), so a card spliced in — e.g. the BnBerry recommendation at slot 3 — doesn't leave
  // the siblings it shifted right reporting a stale, colliding position captured earlier.
  //
  // Caveat since the EO-1287 interleave: useHeapCardViewed fires "Card Viewed" once per card
  // and then unobserves, so a v1 card already on screen when the slower v2 read lands has
  // already reported its pre-interleave position, and that is the value Heap keeps. The
  // position here is right for the render; it is not necessarily the one that was sent.
  const positionedCards = allCards.map((card, index) =>
    React.cloneElement(card, { cardPosition: index + 1 })
  );

  // Determine if carousel is needed
  const useCarousel = allCards.length > MAX_CARDS;

  // Calculate max scroll index (how far we can scroll)
  const maxScrollIndex = Math.max(0, allCards.length - CARDS_PER_SLIDE);

  const next = () => {
    setCurrentIndex((prev) => {
      const nextIdx = prev + 1;
      return nextIdx > maxScrollIndex ? prev : nextIdx;
    });
  };

  const previous = () => {
    setCurrentIndex((prev) => {
      const prevIdx = prev - 1;
      return prevIdx < 0 ? prev : prevIdx;
    });
  };

  // Reset scroll position when cards change
  React.useEffect(() => {
    if (currentIndex > maxScrollIndex) {
      setCurrentIndex(0);
    }
  }, [currentIndex, maxScrollIndex]);

  return (
    <section className="action-center">
      <div className="action-center-header">
        <div className="action-center-header-left">
          <div className="action-center-title-row">
            <h2>Action Center</h2>
            {feedsSettled && actionCount > 0 && (
              <span
                className="action-center-count"
                data-testid="action-center-count"
              >
                {actionCount}
              </span>
            )}
          </div>
          {feedsSettled && actionCount > 0 && (
            <span className="action-center-subtitle">
              Opportunities for you to review
            </span>
          )}
        </div>
        {useCarousel && (
          <div className="action-center-carousel-controls">
            <button
              type="button"
              className="carousel-control-btn carousel-control-prev"
              onClick={previous}
              aria-label="Previous"
              disabled={currentIndex === 0}
            >
              <ChevronLeftIcon className="carousel-control-icon" />
            </button>
            <button
              type="button"
              className="carousel-control-btn carousel-control-next"
              onClick={next}
              aria-label="Next"
              disabled={currentIndex >= maxScrollIndex}
            >
              <ChevronRightIcon className="carousel-control-icon" />
            </button>
          </div>
        )}
      </div>
      {showLoading ? (
        <div
          className="action-center-loading"
          data-testid="action-center-loading"
        >
          <HTLoader loaderText="" />
        </div>
      ) : useCarousel ? (
        <div className="action-center-carousel-container">
          <div
            className="action-center-cards-slider"
            style={{
              transform: `translateX(calc(-${currentIndex} * (100% / 3 + 16px / 3)))`,
            }}
          >
            {positionedCards.map((card) => card)}
          </div>
        </div>
      ) : (
        <div className="action-center-cards-grid">{positionedCards}</div>
      )}
    </section>
  );
};

ActionCenter.propTypes = {
  data: PropTypes.shape({
    propertyId: PropTypes.number,
    results: PropTypes.shape({
      CORRIDOR: PropTypes.array,
      CORRIDOR_REQUEST: PropTypes.array,
      ARI: PropTypes.array,
      PROMO: PropTypes.array,
      BNBERRY: PropTypes.array,
    }),
  }),
  v2Data: PropTypes.shape({
    propertyId: PropTypes.number,
    cards: PropTypes.array,
  }),
  loading: PropTypes.bool,
  v2Loading: PropTypes.bool,
  feedsSettled: PropTypes.bool,
  selectedProperty: PropTypes.object,
  onPromoUsed: PropTypes.func,
  pinnedRecommendationId: PropTypes.string,
  openRecommendationId: PropTypes.string,
  onRecommendationOpened: PropTypes.func,
  onRecommendationDismissed: PropTypes.func,
};

export default ActionCenter;
